import { useEffect } from 'react';
import { useAppDispatch } from '../store/hooks';
import { clearLocationPermissions } from '../store/permissions/permissionsSlice';
import { fetchAccountPermissions, fetchLocationPermissions } from '../services/permissionsService';

/**
 * Loads what the signed-in user may do at one location.
 *
 * Called once from the root layout for the active location, so every page and
 * the navigation itself read the same answer.
 */
export function useFetchLocationPermissions(locationId: string | undefined | null): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Clear first: without this, moving from a location the user may plan in to
    // one they may only read shows the write controls until the new response
    // lands.
    dispatch(clearLocationPermissions());

    if (!locationId) return;

    const controller = new AbortController();
    void fetchLocationPermissions(dispatch, locationId, controller.signal);

    return () => {
      controller.abort();
    };
  }, [locationId, dispatch]);
}

/**
 * Loads what the signed-in user may do account-wide: the shared configuration,
 * the locations and the users.
 *
 * Account permissions are the union across every location the user holds a role
 * at — assignments are per location, so being central administration at one
 * centre is what makes you central administration. An account administrator
 * holds all of them outright.
 */
export function useFetchAccountPermissions(): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const controller = new AbortController();
    void fetchAccountPermissions(dispatch, controller.signal);

    return () => {
      controller.abort();
    };
  }, [dispatch]);
}
